import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from '../config/api';
import { formatCurrency } from '../utils/format';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import ConfirmDialog from '../components/ConfirmDialog';
import ReviewModal from '../components/ReviewModal';
import { OrderSkeleton } from '../components/LoadingSkeleton';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { useLivePoll } from '../hooks/useLivePoll';
import EmptyState from '../components/EmptyState';
import CafeAvatar from '../components/CafeAvatar';
import ReportOrderIssueModal from '../components/ReportOrderIssueModal';
import { ISSUE_STATUS_LABELS } from '../content/orderIssueTypes';
import DeliveryOrderSummary from '../components/DeliveryOrderSummary';
import { deliveryFieldsFromPending } from '../utils/deliveryDropoff';
import OrderDineInCode from '../components/OrderDineInCode';

interface OrderItem {
  menu_item_id: string;
  quantity: number;
  name?: string;
}

interface Order {
  id: string;
  order_number?: string;
  cafe_id: string;
  cafe_name: string;
  cafe_address: string;
  cafe_image_url?: string | null;
  cafe_subscription_active?: number | boolean;
  items: OrderItem[];
  total_amount: number;
  points_earned: number;
  points_used: number;
  student_discount?: number;
  status: string;
  name?: string;
  payment_method?: string;
  created_at: string;
  has_review?: number;
  issue_id?: string | null;
  issue_status?: string | null;
  fulfillment_type?: string | null;
  dine_in_qr_token?: string | null;
  delivery_address?: string | null;
  delivery_distance_km?: number | null;
}

function formatOrderDateTime(iso: string): string {
  const d = new Date(iso);
  const month = d.toLocaleString('en-US', { month: 'short' });
  const day = String(d.getDate()).padStart(2, '0');
  const time = d
    .toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    .replace(' AM', 'am')
    .replace(' PM', 'pm');
  return `${month} ${day} • ${time}`;
}

function statusLabel(status: string): string {
  const map: Record<string, string> = {
    completed: 'Delivered',
    pending: 'Pending',
    received: 'Received',
    cancelled: 'Cancelled',
    preparing: 'Preparing',
    ready: 'Ready',
  };
  return map[status] || status.charAt(0).toUpperCase() + status.slice(1);
}

function statusBadgeClass(status: string): string {
  if (status === 'completed') return 'bg-gray-100 text-gray-700';
  if (status === 'pending' || status === 'received') return 'bg-amber-50 text-amber-800';
  if (status === 'cancelled') return 'bg-red-50 text-red-700';
  return 'bg-gray-100 text-gray-700';
}

function itemCount(items: OrderItem[]): number {
  return items.reduce((sum, i) => sum + (i.quantity || 0), 0);
}

function OrderCard({
  order,
  cafeImageUrl,
  expanded,
  onToggleItems,
  onCancel,
  onReorder,
  onRate,
  onReportIssue,
}: {
  order: Order;
  cafeImageUrl?: string | null;
  expanded: boolean;
  onToggleItems: () => void;
  onCancel: () => void;
  onReorder: () => void;
  onRate: () => void;
  onReportIssue: () => void;
}) {
  const count = itemCount(order.items);
  const isPremium = !!order.cafe_subscription_active;
  const canCancel = order.status === 'pending' || order.status === 'received';
  const showRateFooter = order.status === 'completed' && !order.has_review;
  const isCompleted = order.status === 'completed';
  const hasIssueReport = !!order.issue_id;

  return (
    <article className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden dark:bg-[#1e120e] dark:border-gray-700">
      <div className="px-4 pt-4 pb-3 flex items-center justify-between gap-3">
        <span
          className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${statusBadgeClass(order.status)}`}
        >
          {statusLabel(order.status)}
        </span>
        <time className="text-sm text-gray-500 shrink-0" dateTime={order.created_at}>
          {formatOrderDateTime(order.created_at)}
        </time>
      </div>

      <div className="px-4 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex gap-3 min-w-0 flex-1">
            <CafeAvatar imageUrl={cafeImageUrl} name={order.cafe_name} />
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <h3 className="font-bold text-gray-900 dark:text-gray-100 truncate">
                  {order.cafe_name}
                </h3>
                {isPremium && (
                  <span className="shrink-0 bg-violet-600 text-white text-[10px] font-bold uppercase px-1.5 py-0.5 rounded">
                    pro
                  </span>
                )}
              </div>
              {order.order_number && (
                <p className="text-sm text-gray-500 mt-0.5">
                  Order ID: {order.order_number}
                </p>
              )}
              {order.name && (
                <p className="text-sm text-amber-800 dark:text-amber-200 mt-0.5 truncate">
                  {order.name}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onToggleItems}
            className="shrink-0 flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300 pt-1"
            aria-expanded={expanded}
          >
            <span>
              {count} {count === 1 ? 'item' : 'items'}
            </span>
            <span
              className={`text-[10px] leading-none transition-transform inline-block ${expanded ? 'rotate-180' : ''}`}
              aria-hidden
            >
              ▼
            </span>
          </button>
        </div>

        {expanded && (
          <div className="mt-3 pl-[3.75rem] space-y-2 text-sm">
            <ul className="space-y-1 text-gray-600 dark:text-gray-400">
              {order.items.map((item, idx) => (
                <li key={idx}>
                  {item.quantity}× {item.name || `Item #${item.menu_item_id.substring(0, 8)}`}
                </li>
              ))}
            </ul>
            {order.cafe_address && (
              <p className="text-gray-500 text-xs">📍 {order.cafe_address}</p>
            )}
            <DeliveryOrderSummary order={order} />
            {order.payment_method && (
              <p className="text-gray-500 text-xs">
                {order.payment_method === 'points' ? '🎁' : '💳'} Payment:{' '}
                {order.payment_method.charAt(0).toUpperCase() + order.payment_method.slice(1)}
              </p>
            )}
            {(order.status !== 'cancelled' && order.points_earned > 0) ||
            order.points_used > 0 ? (
              <p className="text-xs">
                {order.status !== 'cancelled' && order.points_earned > 0 && (
                  <span className="text-green-600">+{order.points_earned} points earned</span>
                )}
                {order.points_used > 0 && (
                  <span className="text-amber-600 ml-2">{order.points_used} points used</span>
                )}
              </p>
            ) : null}
            {canCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="text-red-600 text-xs font-semibold underline"
              >
                Cancel order
              </button>
            )}
          </div>
        )}

        <div className="flex items-end justify-between gap-3 mt-4">
          <div>
            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(order.total_amount)}
            </p>
            <Link
              to={`/orders/${order.id}`}
              className="text-sm text-gray-600 underline mt-0.5 inline-block dark:text-gray-400"
            >
              View details
            </Link>
          </div>
          <button
            type="button"
            onClick={onReorder}
            className="shrink-0 px-5 py-2 rounded-full border border-gray-900 text-sm font-semibold text-gray-900 hover:bg-gray-50 transition dark:border-gray-300 dark:text-gray-100 dark:hover:bg-gray-800"
            style={{ touchAction: 'manipulation' }}
          >
            Order again
          </button>
        </div>
        <OrderDineInCode order={order} />

        {isCompleted && (
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            {hasIssueReport ? (
              <button
                type="button"
                onClick={onReportIssue}
                className="w-full py-2.5 text-sm font-semibold text-amber-900 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition text-left px-3 dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-100"
                style={{ touchAction: 'manipulation' }}
              >
                <span className="block">View report status</span>
                {order.issue_status && (
                  <span className="block text-xs font-normal text-amber-800/90 dark:text-amber-200/90 mt-0.5">
                    {ISSUE_STATUS_LABELS[order.issue_status] || order.issue_status}
                  </span>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={onReportIssue}
                className="w-full py-2 text-sm font-semibold text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition dark:border-gray-600 dark:text-gray-100"
                style={{ touchAction: 'manipulation' }}
              >
                Report an issue
              </button>
            )}
          </div>
        )}
      </div>

      {showRateFooter && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between dark:bg-[#24130f] dark:border-gray-700">
          <span className="font-bold text-gray-900 dark:text-gray-100">Rate</span>
          <div className="flex items-center gap-1" role="group" aria-label="Rate this order">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={onRate}
                className="p-1 text-gray-400 hover:text-amber-600 transition"
                aria-label={`Rate ${star} stars`}
              >
                <span className="text-xl text-gray-400 leading-none" aria-hidden>
                  ☆
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}

export default function Orders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { addItem, clearCart } = useCart();
  const { updateUser } = useAuth();
  const [cancelOrderId, setCancelOrderId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reviewOrder, setReviewOrder] = useState<Order | null>(null);
  const [issueOrder, setIssueOrder] = useState<Order | null>(null);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const [cafeImagesById, setCafeImagesById] = useState<Record<string, string>>({});

  const pageBottomPad =
    'pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] lg:pb-8';

  useEffect(() => {
    fetchOrders();
    const timer = setTimeout(() => {
      checkPendingPayment();
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const checkPendingPayment = async () => {
    const pendingPaymentId = sessionStorage.getItem('pendingPaymentId');
    const pendingPaymentType = sessionStorage.getItem('pendingPaymentType');
    const pendingOrderDataStr = sessionStorage.getItem('pendingOrderData');

    const urlParams = new URLSearchParams(window.location.search);
    const isPaymentReturn = urlParams.get('payment_return') === 'true';

    if ((pendingPaymentId && pendingPaymentType === 'order' && pendingOrderDataStr) || isPaymentReturn) {
      if (isPaymentReturn && !pendingPaymentId) {
        showToast('Payment was cancelled or not completed.', 'info');
        window.history.replaceState({}, '', '/orders');
        return;
      }

      if (!pendingPaymentId || !pendingOrderDataStr) {
        return;
      }
      try {
        const confirmResponse = await axios.post('/api/payments/confirm', {
          paymentIntentId: pendingPaymentId,
        }, {
          timeout: 30000,
        });

        if (confirmResponse.status === 202 && confirmResponse.data.pending) {
          showToast('Payment is still in progress. Please complete it on the secure payment page.', 'info');
          return;
        }

        if (confirmResponse.data.success) {
          const pendingOrderData = JSON.parse(pendingOrderDataStr);

          try {
            const orderResponse = await axios.post('/api/orders', {
              cafe_id: pendingOrderData.cafeId,
              items: pendingOrderData.items,
              points_used: pendingOrderData.pointsUsed || 0,
              wallet_used: pendingOrderData.walletUsed || 0,
              name: pendingOrderData.orderName,
              payment_method: 'visa',
              payment_intent_id: pendingPaymentId,
              ...(pendingOrderData.promotionId
                ? { promotion_id: pendingOrderData.promotionId }
                : {}),
              fulfillment_type: pendingOrderData.fulfillmentType || 'pickup',
              ...deliveryFieldsFromPending(pendingOrderData),
            });

            const orderNumber = orderResponse.data.order_number || '';
            const pointsEarned = orderResponse.data.points_earned || 0;

            showToast(
              `Payment successful! Order #${orderNumber} placed. You earned ${pointsEarned} points!`,
              'success'
            );

            clearCart();
            sessionStorage.removeItem('pendingPaymentId');
            sessionStorage.removeItem('pendingPaymentType');
            sessionStorage.removeItem('pendingPaymentAmount');
            sessionStorage.removeItem('pendingOrderData');

            fetchOrders();

            setTimeout(() => {
              window.location.reload();
            }, 1000);
          } catch (orderError: any) {
            console.error('Error creating order after payment:', orderError);
            const errorMsg = orderError.response?.data?.error || 'Order creation failed';
            showToast(`Payment successful but order creation failed: ${errorMsg}. Please contact support.`, 'error');
          }
        } else {
          showToast('Payment verification failed. Please try again.', 'error');
          sessionStorage.removeItem('pendingPaymentId');
          sessionStorage.removeItem('pendingPaymentType');
          sessionStorage.removeItem('pendingPaymentAmount');
          sessionStorage.removeItem('pendingOrderData');
          window.history.replaceState({}, '', '/orders');
        }
      } catch (error: any) {
        console.error('Payment verification error:', error);
        const errorMessage = error.response?.data?.error || error.message || 'Unable to verify payment';

        if (error.response?.status === 404 || errorMessage.includes('not found')) {
          showToast('Payment is still processing. Please wait a moment and refresh.', 'info');
        } else if (errorMessage.includes('timeout')) {
          console.log('Payment verification timeout - may still be processing');
        } else {
          showToast(`Payment verification failed: ${errorMessage}. Please check your orders or contact support.`, 'error');
          sessionStorage.removeItem('pendingPaymentId');
          sessionStorage.removeItem('pendingPaymentType');
          sessionStorage.removeItem('pendingPaymentAmount');
          sessionStorage.removeItem('pendingOrderData');
        }
        window.history.replaceState({}, '', '/orders');
      }
    }
  };

  const fetchOrders = async () => {
    try {
      setError(null);
      const [ordersResponse, cafesResponse] = await Promise.all([
        axios.get('/api/orders/my-orders'),
        axios.get('/api/cafes').catch(() => ({ data: [] as { id: string; image_url?: string }[] })),
      ]);
      setOrders(ordersResponse.data);
      const images: Record<string, string> = {};
      for (const cafe of cafesResponse.data || []) {
        if (cafe.id && cafe.image_url) {
          images[cafe.id] = cafe.image_url;
        }
      }
      setCafeImagesById(images);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrdersRef = useRef(fetchOrders);
  fetchOrdersRef.current = fetchOrders;
  useLivePoll(() => {
    void fetchOrdersRef.current();
  }, 5000);

  const { isRefreshing, pullDistance, pullProgress } = usePullToRefresh({
    onRefresh: async () => {
      setLoading(true);
      await fetchOrders();
    },
    disabled: loading
  });

  const handleCancelOrder = async (orderId: string) => {
    setCancelOrderId(orderId);
  };

  const confirmCancelOrder = async () => {
    if (!cancelOrderId) return;

    try {
      await axios.put(`/api/orders/${cancelOrderId}/cancel`);
      showToast('Order cancelled successfully!', 'success');
      setCancelOrderId(null);
      await updateUser();
      fetchOrders();
    } catch (error: any) {
      console.error('Error cancelling order:', error);
      showToast(error.response?.data?.error || 'Failed to cancel order', 'error');
      setCancelOrderId(null);
    }
  };

  const handleReorder = async (order: Order) => {
    try {
      if (!order.cafe_id) {
        showToast('Cafe information not available. Cannot reorder.', 'error');
        return;
      }

      clearCart();

      const menuResponse = await axios.get(`/api/cafes/${order.cafe_id}/menu`);
      const menuItems = menuResponse.data;

      for (const item of order.items as any[]) {
        const menuItem = menuItems.find((m: any) => m.id === item.menu_item_id);

        if (menuItem) {
          addItem(
            {
              menu_item_id: item.menu_item_id,
              name: menuItem.name,
              price: item.price || menuItem.price,
              quantity: item.quantity,
              category: menuItem.category,
              note: item.note,
              customizations: item.customizations,
            },
            order.cafe_id
          );
        } else {
          console.warn(`Menu item ${item.menu_item_id} not found`);
        }
      }

      navigate('/cart');
      showToast('Items added to cart!', 'success');
    } catch (error) {
      console.error('Error reordering:', error);
      showToast('Failed to reorder. Please try again.', 'error');
    }
  };

  const toggleItems = (orderId: string) => {
    setExpandedItems((prev) => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  return (
    <div className={`container mx-auto px-4 py-8 relative ${pageBottomPad}`}>
      {isRefreshing && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 bg-amber-700 text-white px-4 py-2 rounded-full shadow-lg z-50">
          <span className="animate-spin mr-2">⟳</span> Refreshing...
        </div>
      )}

      {pullDistance > 10 && !isRefreshing && (
        <div
          className="fixed top-20 left-1/2 transform -translate-x-1/2 text-amber-700 text-sm z-50"
          style={{ opacity: Math.min(pullProgress, 1) }}
        >
          Pull to refresh {pullProgress >= 1 ? '✓' : '↓'}
        </div>
      )}

      <h1 className="text-3xl font-bold mb-2 text-amber-800">Order History</h1>
      <p className="text-sm text-gray-600 mb-6">
        Need help? Email{' '}
        <a className="text-amber-800 font-semibold" href="mailto:support@qahwahub.com">
          support@qahwahub.com
        </a>
      </p>

      {loading && orders.length === 0 ? (
        <div className="space-y-4">
          <OrderSkeleton />
          <OrderSkeleton />
          <OrderSkeleton />
        </div>
      ) : (
        <>
          {orders.length === 0 ? (
            <EmptyState
              icon="📦"
              title="No orders yet"
              message="Your order history will appear here once you place your first order!"
              actionLabel="Browse Cafes"
              actionPath="/cafes"
            />
          ) : (
            <div className="space-y-4">
              {orders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  cafeImageUrl={order.cafe_image_url || cafeImagesById[order.cafe_id]}
                  expanded={!!expandedItems[order.id]}
                  onToggleItems={() => toggleItems(order.id)}
                  onCancel={() => handleCancelOrder(order.id)}
                  onReorder={() => handleReorder(order)}
                  onRate={() => setReviewOrder(order)}
                  onReportIssue={() => setIssueOrder(order)}
                />
              ))}
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={!!cancelOrderId}
        title="Cancel Order"
        message="Are you sure you want to cancel this order? This action cannot be undone."
        confirmText="Cancel Order"
        cancelText="Keep Order"
        type="danger"
        onConfirm={confirmCancelOrder}
        onCancel={() => setCancelOrderId(null)}
      />

      {issueOrder && (
        <ReportOrderIssueModal
          order={issueOrder}
          isOpen={!!issueOrder}
          onClose={() => setIssueOrder(null)}
          onSubmitted={() => fetchOrders()}
        />
      )}

      {reviewOrder && (
        <ReviewModal
          orderId={reviewOrder.id}
          cafeName={reviewOrder.cafe_name}
          orderItems={reviewOrder.items}
          isOpen={!!reviewOrder}
          onClose={() => setReviewOrder(null)}
          onReviewSubmitted={() => {
            setReviewOrder(null);
            fetchOrders();
          }}
        />
      )}
    </div>
  );
}
